import {
  FormField,
  Select,
} from '@cloudscape-design/components';
import { PPartRetiredStatuses } from '@parallel-fluidics/shared/constants';
import { getFullPPartId } from '@parallel-fluidics/shared/models/projects/parts';
import usePistonParts from 'features/pPart/hooks/usePistonParts';
import getPistonSummary from 'features/pPart/utils/getPistonSummary';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

/**
 * Piston picker for the forming start-run modal. Lists piston parts from
 * every project; the selected value is the full pPartId including the
 * owning project's name ("P072-0001-A-PN12-1") because, unlike tools, a
 * piston may live in a different project than the run. A piston linked
 * from the step's product part is auto-selected (still overridable), like
 * the tool selector does for tools.
 */
export default function PistonSelector({
  enabled,
  handleChange,
  selectedValue = null,
  errorText = null,
  linkedParts = [],
  stepKey = '',
}) {
  const {
    data: pistonRows = [], error, isFetching, isSuccess,
  } = usePistonParts({ enabled });

  const isLinked = useCallback(({ piston, projectDocId }) => linkedParts.some(
    (row) => row.projectDocId === projectDocId && row.pPartDocId === piston.id,
  ), [linkedParts]);

  const options = useMemo(() => pistonRows
    .filter(({ piston }) => !PPartRetiredStatuses.includes(piston.status))
    .map((row) => {
      const fullId = getFullPPartId(row.piston, row.projectName);
      return {
        value: fullId,
        label: fullId,
        labelTag: row.piston.status,
        description: getPistonSummary(row.piston),
        linked: isLinked(row),
      };
    }), [pistonRows, isLinked]);

  // The form keeps the selection across modal closes (and "load previous
  // configuration" can set one from an old log). After each successful
  // refresh, clear any selection that isn't a selectable option — retired,
  // deleted, invalid, or a Notion-era name — so a run can't record a piston
  // that no longer exists as a part. A failed refresh leaves it alone.
  // Declared before the auto-select effect: effects run in order, so when
  // the selected piston retires while a linked replacement exists, the
  // clear happens first and the auto-selected replacement wins.
  useEffect(() => {
    if (!selectedValue || !isSuccess || isFetching) return;
    if (!options.some((option) => option.value === selectedValue)) handleChange('');
  }, [options, selectedValue, isSuccess, isFetching, handleChange]);

  // Auto-select when exactly one linked piston is offered. Keyed on the
  // step plus its linked set (not on `options`, which is rebuilt on every
  // refetch) so it fires once per step change — including between two
  // steps that link the same piston — and an operator's override sticks
  // within a step. JSON-encoded: ids may contain any character but "-".
  const linkedKey = useMemo(() => JSON.stringify({
    step: stepKey,
    linked: options.filter((option) => option.linked).map((option) => option.value),
  }), [options, stepKey]);
  useEffect(() => {
    const { linked } = JSON.parse(linkedKey);
    if (linked.length === 1) handleChange(linked[0]);
  }, [linkedKey, handleChange]);

  // A linked piston that's hidden (Obsolete/Broken) can't be auto-selected —
  // say so instead of leaving the field silently empty.
  const warningText = useMemo(() => {
    const hiddenLinked = pistonRows
      .filter((row) => PPartRetiredStatuses.includes(row.piston.status) && isLinked(row));
    if (!hiddenLinked.length) return undefined;
    return hiddenLinked
      .map((row) => `Linked piston ${getFullPPartId(row.piston, row.projectName)} is ${row.piston.status}`)
      .join('. ');
  }, [pistonRows, isLinked]);

  // Shown as-is until the refresh above settles, then either matched or cleared.
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return options.find((option) => option.value === selectedValue)
      || { value: selectedValue, label: selectedValue };
  }, [options, selectedValue]);

  let statusType = 'finished';
  if (error) {
    statusType = 'error';
  } else if (isFetching) {
    statusType = 'loading';
  }

  return (
    <FormField
      label="Piston"
      errorText={errorText}
      warningText={warningText}
      constraintText={(
        <span>
          {'Don\'t see the piston you\'re looking for? Add it as a Piston part in its project '}
          <a target="_blank" href="/projects?type=internal" rel="noreferrer">here</a>
          .
        </span>
      )}
    >
      <Select
        selectedOption={selectedOption}
        onChange={({ detail }) => handleChange(detail.selectedOption.value)}
        errorText={error ? `${error.message}` : undefined}
        filteringType="auto"
        options={options}
        loadingText="Loading pistons"
        placeholder="Choose a piston"
        empty="No pistons found"
        statusType={statusType}
      />
    </FormField>
  );
}

PistonSelector.propTypes = {
  /** Whether to fetch pistons; each false→true transition refetches the list. */
  enabled: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  selectedValue: PropTypes.string,
  errorText: PropTypes.string,
  linkedParts: PropTypes.arrayOf(PropTypes.shape({
    projectDocId: PropTypes.string,
    pPartDocId: PropTypes.string,
  })),
  /** Identity of the run context (line item + step) the links belong to; scopes auto-selection. */
  stepKey: PropTypes.string,
};
